import type { ReactNode } from 'react';

import { onLinkClick } from '../app/router.js';
import { useT } from '../i18n/index.js';
import { themeStore, uiLangStore, useResolvedTheme, useUiLang } from '../state/prefs.js';
import { Icon } from './Icon.js';

export function Brand() {
  const t = useT();
  return (
    <a href="/" className="brand" onClick={onLinkClick}>
      <span className="brand-mark">
        <Icon name="logo" size={16} strokeWidth={2.4} />
      </span>
      <span className="brand-name">{t.app.name}</span>
    </a>
  );
}

export function LangSwitch() {
  const t = useT();
  const lang = useUiLang();
  return (
    <div className="segmented" role="group" aria-label={t.lang.label}>
      {(['en', 'ro'] as const).map((l) => (
        <button key={l} aria-pressed={lang === l} onClick={() => uiLangStore.set(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function ThemeToggle() {
  const t = useT();
  const theme = useResolvedTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="icon-btn"
      onClick={() => themeStore.set(next)}
      aria-label={next === 'dark' ? t.theme.toDark : t.theme.toLight}
      title={next === 'dark' ? t.theme.toDark : t.theme.toLight}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
    </button>
  );
}

/** The 56px bar from the mockups: brand, optional breadcrumb, then right-aligned actions. */
export function TopBar({ crumbs, children }: { crumbs?: ReactNode; children?: ReactNode }) {
  const t = useT();
  return (
    <header className="topbar">
      <Brand />
      {crumbs && (
        <>
          <div className="topbar-divider" />
          <nav className="crumbs" aria-label={t.common.breadcrumb}>
            {crumbs}
          </nav>
        </>
      )}
      <div className="topbar-spacer" />
      {children}
    </header>
  );
}

export function Crumbs({ parts }: { parts: string[] }) {
  return (
    <>
      {parts.map((p, i) => (
        <span key={i} className={i === parts.length - 1 ? 'crumb-current' : 'crumb'}>
          {i > 0 && <Icon name="chevronRight" size={14} strokeWidth={2.5} className="crumb-sep" />}
          {p}
        </span>
      ))}
    </>
  );
}
